import type { AxiosError } from 'axios';

type ApiErrorBody = {
  error?: string;
  detail?: string;
  details?: string[];
  message?: string;
};

/** Logs axios failures without request body (avoids leaking passwords into the console). */
export function logSafeAxiosError(context: string, err: unknown): void {
  const e = err as AxiosError<ApiErrorBody | string>;
  if (e?.isAxiosError) {
    console.error(context, {
      status: e.response?.status,
      data: e.response?.data,
      code: e.code,
      message: e.message,
    });
    return;
  }
  console.error(context, err);
}

function truncate(s: string, max = 400): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function parseErrorBody(
  data: unknown,
  httpStatus?: number
): { error?: string; detail?: string } {
  if (data == null) return {};
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) return {};
    const lower = trimmed.toLowerCase();
    if (
      (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) &&
      (lower.includes('cloudflare') ||
        lower.includes('origin web server') ||
        lower.includes('bad gateway') ||
        lower.includes('error code'))
    ) {
      return {
        error: 'EmailCraft API did not respond (gateway / proxy error)',
        detail:
          'A proxy or gateway in front of EmailCraft returned an empty or timed-out response while loading Hostlinks events.\n' +
          'On the VPS: pm2 logs emailcraft-api --lines 80 — look for [hostlinks] or [events/upcoming].\n' +
          'Then test Hostlinks directly: curl -H "X-HL-Key: YOUR_KEY" ".../wp-json/hostlinks/v1/email-events?days=7"',
      };
    }
    if (trimmed.startsWith('<!') || trimmed.startsWith('<html') || lower.includes('<!doctype')) {
      return {
        error: 'Server returned HTML instead of JSON (proxy or gateway error)',
        detail: truncate(trimmed.replace(/\s+/g, ' ')),
      };
    }
    return { error: truncate(trimmed) };
  }
  if (typeof data !== 'object') return {};
  const d = data as ApiErrorBody;
  const error =
    (typeof d.error === 'string' && d.error) ||
    (typeof d.message === 'string' && d.message) ||
    undefined;
  const detailParts: string[] = [];
  if (typeof d.detail === 'string' && d.detail) detailParts.push(d.detail);
  if (Array.isArray(d.details) && d.details.length > 0) {
    detailParts.push(d.details.join('; '));
  }
  return {
    error,
    detail: detailParts.length > 0 ? detailParts.join(' — ') : undefined,
  };
}

/** User-facing message from an axios or network failure (includes HTTP status and server detail when available). */
export function getAxiosErrorMessage(err: unknown, fallback: string): string {
  const e = err as AxiosError<ApiErrorBody | string>;
  if (!e?.isAxiosError) {
    if (err instanceof Error && err.message) return `${fallback}: ${err.message}`;
    return fallback;
  }

  const status = e.response?.status;
  const { error: bodyError, detail } = parseErrorBody(e.response?.data, status);

  if (!e.response) {
    const code = e.code ? ` (${e.code})` : '';
    const msg = e.message && e.message !== 'Network Error' ? `: ${e.message}` : '';
    return `${fallback}${code}${msg}`.trim();
  }

  const parts: string[] = [];
  if (bodyError) {
    parts.push(bodyError);
  } else if (status) {
    parts.push(`${fallback} (HTTP ${status})`);
  } else {
    parts.push(fallback);
  }
  if (detail && detail !== bodyError) parts.push(detail);
  if (status && bodyError && !bodyError.includes(`HTTP ${status}`)) {
    parts.push(`HTTP ${status}`);
  }
  return parts.join(' — ');
}

/** Split headline + detail for multi-line error UI. */
export function getApiErrorParts(
  err: unknown,
  fallback: string
): { headline: string; detail?: string } {
  const e = err as AxiosError<ApiErrorBody | string>;
  if (!e?.isAxiosError) {
    return {
      headline: err instanceof Error ? `${fallback}: ${err.message}` : fallback,
    };
  }

  const status = e.response?.status;
  const { error: bodyError, detail } = parseErrorBody(e.response?.data, status);

  if (!e.response) {
    const code = e.code ? ` (${e.code})` : '';
    const msg =
      e.message && e.message !== 'Network Error' ? ` — ${e.message}` : '';
    return { headline: `${fallback}${code}${msg}`.trim() };
  }

  const headline =
    bodyError ||
    (status ? `${fallback} (HTTP ${status})` : fallback);

  const detailParts: string[] = [];
  if (detail && detail !== bodyError) detailParts.push(detail);
  if (status && bodyError && !bodyError.includes(String(status))) {
    detailParts.push(`EmailCraft API HTTP ${status}`);
  }

  return {
    headline,
    detail: detailParts.length > 0 ? detailParts.join('\n') : undefined,
  };
}
